import React, { useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import { useGetProfilesForCaseQuery } from "../../graphql/supergraphHooks";
import { isJoinAccountType, resolvedAccountType } from "../../utils/account_types";
import { isAmlProgram } from "../../utils/compliance_program";
import { clientContext } from "../../utils/graphql_utils";
import { IndividualForm } from "../IndividualForm";
import { IndividualProfile } from "../IndividualForm/types";
import { useUpdateProfile } from "../profile/hooks";
import { provideProfileName } from "../ProfileTree/utils";
import { prepareIndividualPayload } from "./utils";

/**
 * `IndividualProfileEditScreen` is a container component that wraps the `IndividualForm` component
 * and adds the business logic for submitting a GraphQL mutation when the form is submitted.
 *
 * This component handles:
 * 1. Loading the individual profile data
 * 2. Transforming the form data to match the GraphQL mutation input
 * 3. Submitting the mutation
 * 4. Handling success and error states
 * 5. Navigation after form submission
 */
export const IndividualProfileEditScreen = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId, basePath, caseId, programId, skipDetailsForRootProfile } =
    useComplianceCaseContext();
  const isRoot = Number(profileId) === rootProfileId;
  const history = useHistory();

  const profileAddressResult = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseId: caseId,
    },
  });

  const complianceCase = profileAddressResult.data?.complianceCase;
  const allProfiles = complianceCase?.profiles.concat([complianceCase?.investingProfile]);
  const profile = allProfiles?.find((p) => p?.id === parseInt(profileId));
  const parentProfileId = profile?.parentRelationships?.[0]?.parentProfileId;

  const isChildOfJointAccount = useMemo(() => {
    if (!parentProfileId) return false;
    const profile = allProfiles?.find((p) => p?.id === parentProfileId);
    const accountType = resolvedAccountType(profile);

    return isJoinAccountType(accountType);
  }, [allProfiles, parentProfileId]);

  const profileName = useMemo(() => {
    return provideProfileName(profile);
  }, [profile]);

  const { updateIndividual, isUpdating } = useUpdateProfile(
    Number(profileId),
    Number(parentProfileId),
    () => {
      if (skipDetailsForRootProfile) {
        history.push(isAmlProgram(programId) ? `${basePath}/overview/${profileId}` : basePath);
      } else {
        history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
      }
    },
  );

  const handleFormSubmit = async (values: IndividualProfile) => {
    try {
      // Only include taxId if it has changed from the initial value
      const taxId =
        values.identificationNumber !== initialValues.identificationNumber
          ? values.identificationNumber || null
          : undefined;

      await updateIndividual(prepareIndividualPayload(caseId, values, profile, taxId));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Error updating individual profile: ${errorMessage}`);
    }
  };

  const handleBack = () => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  };

  const initialValues: IndividualProfile = {
    firstName: profile?.firstName || "",
    middleName: profile?.middleName || "",
    lastName: profile?.lastName || "",
    dateOfBirth: profile?.dateOfBirth ? new Date(profile.dateOfBirth) : null,
    identificationNumber: profile?.taxIdUnencrypted || "",
    accountType: resolvedAccountType(profile),
    streetLine1: profile?.addresses?.[0]?.street || "",
    streetLine2: profile?.addresses?.[0]?.street2 || "",
    city: profile?.addresses?.[0]?.city || "",
    state: profile?.addresses?.[0]?.state?.code || "",
    province: profile?.addresses?.[0]?.province || "",
    zip: profile?.addresses?.[0]?.postalCode || "",
    country: profile?.addresses?.[0]?.country?.alpha2code || "",
  };

  return (
    <QueryLoader result={profileAddressResult}>
      <IndividualForm
        initialValues={initialValues}
        isUpdating={isUpdating}
        onContinue={handleFormSubmit}
        onBack={handleBack}
        profileName={profileName}
        showAccountTypeDropdown={!isChildOfJointAccount}
      />
    </QueryLoader>
  );
};
